export abstract class ApiError extends Error {
  constructor(
    message: string,
    readonly originalError?: unknown,
  ) {
    super(message);
    this.name = new.target.name;
  }
}

// ---- transport errors (produced by Transport) ----

/** 401. Every guard answers the same way: missing, expired or wrong-audience token, or a role the route requires. */
export class ApiNotAuthorized extends ApiError {
  constructor(readonly requestUrl: string) {
    super(`Not authorized: ${requestUrl}`);
  }
}

/**
 * The request was refused for a reason the caller can act on, named by `code`.
 *
 * Two wire shapes land here. The validation tiers answer 400 with a bare
 * `[{code, incorrectValue, details}]`, one entry each; `details` is the
 * sentence, which becomes `detailedDesc`. Everything else the backend refuses
 * with `{error, code?, ...rest}` (a provider not onboarded, a live-break state
 * refusal on 400/404/409/422) becomes one entry with `error` as the sentence
 * and `rest` as the structured `details`.
 */
export class ValidationError extends ApiError {
  constructor(
    readonly code: string,
    /** Field name when a validation tier named one, else empty. */
    readonly incorrectValue: string,
    /** Human-readable sentence. */
    readonly detailedDesc: string,
    /** Extra fields a state refusal carries beside its code, e.g. `{status}` or `{missing: [...]}`. */
    readonly details: Record<string, unknown>,
    readonly requestUrl: string,
  ) {
    super(`${code}${incorrectValue ? ` (${incorrectValue})` : ""}: ${detailedDesc}
${requestUrl}`);
  }

  // biome-ignore lint/suspicious/noExplicitAny: raw JSON
  static fromValidationEntry(json: any, requestUrl: string): ValidationError {
    return new ValidationError(
      String(json?.code ?? ErrorCode.Rejected),
      String(json?.incorrectValue ?? ""),
      String(json?.details ?? ""),
      {},
      requestUrl,
    );
  }

  static fromErrorBody(body: Record<string, unknown>, requestUrl: string): ValidationError {
    const { error, code, message, statusCode: _status, ...details } = body;
    return new ValidationError(
      typeof code === "string" ? code : ErrorCode.Rejected,
      "",
      String(message ?? error),
      details,
      requestUrl,
    );
  }
}

/**
 * A stream the backend refused to open (a 4xx); `errors` are what `request` would have returned, empty for a body of
 * another shape.
 */
export class StreamRefused extends ApiError {
  constructor(
    readonly errors: TransportError[],
    message = errors.map((e) => e.message).join("\n"),
  ) {
    super(message);
  }
}

/** The body did not match the model's schema: the contract drifted, or the client is behind the backend. */
export class ParsingError extends ApiError {}

/**
 * Every API method's failure mapper names the members it transforms or rejects
 * (`throw new UnexpectedApiError(e)`) and ends with `return e` for the rest, so
 * the remainder lands in the method's declared `Result` union. Adding a member
 * here therefore breaks the build in every method until it takes a position.
 */
export type TransportError = ApiNotAuthorized | ValidationError;

/** The backend refused a call in a way this method never expects: a bug on one side or the other. */
export class UnexpectedApiError extends ApiError {
  constructor(readonly error: TransportError) {
    super(`Unexpected ${error.name}: ${error.message}`, error);
  }
}

/** Codes the backend's `ValidationResult` emits, plus the one given to a refusal that names none. */
export const ErrorCode = {
  ValueRequired: "value-required",
  IncorrectValue: "incorrect-value",
  IncorrectState: "incorrect-state",
  AlreadyExist: "already-exist",
  DoesntExist: "doesnt-exist",
  Rejected: "rejected",
  /** A `@RateLimit` route answered 429: the caller is to wait, not to change anything. */
  RateLimited: "rate_limited",
  /** The caller's shop role does not permit this — a 400, never a 401. */
  Forbidden: "forbidden",
  /** Staff have not switched this on for the shop; `incorrectValue` names the capability. */
  CapabilityRequired: "capability-required",
  /** The shop takes payment through the other provider; `incorrectValue` is the mode the route needs. */
  WrongCommerceMode: "wrong-commerce-mode",
  /** No pricing source knows the product: there is no market estimate. */
  NotLinked: "not-linked",
  /** Fewer units are free than asked for; `details.available` says how many are. */
  InsufficientStock: "insufficient_stock",
  /** A sealed product the shop does not count, so it has nothing to sell from. */
  NotForSale: "not_for_sale",
  /** A count below what carts, quotes and breaks hold. */
  StockReserved: "stock_reserved",
  /** A scan's quote is no longer the caller's to buy: lapsed, withdrawn, sold or never approved. */
  QuoteUnavailable: "quote_unavailable",
} as const;

// ---- domain errors (produced by API groups) ----

/**
 * The backend refused one field, or a request as a whole (`fieldName` empty),
 * and said why. `message` is the backend's own sentence, the one to show; the
 * field name is for a form that wants to attach it to the input.
 */
export class InvalidFormField extends ApiError {
  constructor(
    readonly fieldName: string,
    readonly details: string,
    originalError?: unknown,
  ) {
    super(details, originalError);
  }
}

/** The store does not exist or the caller does not administer it; the backend does not say which. */
export class StoreNotFound extends ApiError {
  constructor(
    readonly storeId: string,
    originalError?: unknown,
  ) {
    super(`Store ${storeId} not found`, originalError);
  }
}

/** No calendar subscription has this id. */
export class CalendarSubscriptionNotFound extends ApiError {
  constructor(
    readonly subscriptionId: string,
    originalError?: unknown,
  ) {
    super(`Calendar subscription ${subscriptionId} not found`, originalError);
  }
}

/** The states given are not all US states, or none was given. `reason` is the sentence to show. */
export class CalendarStatesRefused extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** No event, imported or added, has this id. */
export class EventNotFound extends ApiError {
  constructor(
    readonly eventId: string,
    originalError?: unknown,
  ) {
    super(`Event ${eventId} not found`, originalError);
  }
}

/** No HobbyScan account (other than a storefront guest) uses this address. */
export class AccountNotFound extends ApiError {
  constructor(
    readonly email: string,
    originalError?: unknown,
  ) {
    super(`No account uses ${email}`, originalError);
  }
}

/** Removing this admin was refused: yourself, the last one, or someone not on the list. `reason` is the sentence to show. */
export class AdminRemovalRefused extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** The caller's shop role does not permit this — a Manager assigning or managing above Staff, or anyone below Manager. `reason` is the sentence to show. */
export class StoreRoleForbidden extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

export class CardNotFound extends ApiError {
  constructor(
    readonly cardId: string,
    originalError?: unknown,
  ) {
    super(`Card ${cardId} not found in this shop`, originalError);
  }
}

export class SealedProductNotFound extends ApiError {
  constructor(
    readonly sealedProductId: string,
    originalError?: unknown,
  ) {
    super(`Sealed product ${sealedProductId} not found in this shop`, originalError);
  }
}

/** No pricing source knows the product, so it has no market estimate to refresh. */
export class MarketNotLinked extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** The card is in a state the action cannot apply to: copies on hand, or a customer holding it in a cart. `reason` is the sentence to show. */
export class CardStateRefused extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

export class ScanNotFound extends ApiError {
  constructor(
    readonly scanId: string,
    originalError?: unknown,
  ) {
    super(`Scan ${scanId} not found in this shop`, originalError);
  }
}

/** The scan cannot be reviewed: already confirmed or rejected, or nothing was recognised. `reason` is the sentence to show. */
export class ScanNotReviewable extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** Why a scan could not start: no photo and no usable product barcode, or a Shopify product it cannot read. */
export const SCAN_START_REFUSALS = [
  "invalid-code",
  "photo-required",
  "photo-or-code-required",
  "product-not-scannable",
] as const;
export type ScanStartRefusal = (typeof SCAN_START_REFUSALS)[number];

/** The scan cannot start as sent. `reason` is the sentence to show; `invalid-code` belongs to the barcode field. */
export class ScanStartRefused extends ApiError {
  constructor(
    readonly code: ScanStartRefusal,
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }

  static from(e: ValidationError): ScanStartRefused | null {
    const code = SCAN_START_REFUSALS.find((refusal) => refusal === e.code);
    return code ? new ScanStartRefused(code, e.detailedDesc, e) : null;
  }
}

/** The shared catalogue changed for the scan's codes since it was opened; reload it and review again. */
export class CatalogChanged extends ApiError {
  static readonly code = "CATALOG_CHANGED";

  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** The hand-made item disagrees with the scan on `fields` (e.g. `upc`); pick another or confirm as new. */
export class ItemIdentityConflict extends ApiError {
  static readonly code = "item-identity-conflict";

  constructor(
    readonly fields: string[],
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** `itemId` (null when unknown) already holds the scan's product; linking would make a duplicate. */
export class ItemProductTaken extends ApiError {
  static readonly code = "item-product-taken";

  constructor(
    readonly itemId: string | null,
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** The product named is not one of the scan's candidates. */
export class ScanCandidateUnknown extends ApiError {
  constructor(
    readonly productId: string,
    originalError?: unknown,
  ) {
    super(`Product ${productId} is not one of this scan's candidates`, originalError);
  }
}

/** The cart does not exist, is not open, or the caller may not touch it; the backend does not say which. */
export class CustomerCartNotFound extends ApiError {
  constructor(
    readonly customerCartId: string,
    originalError?: unknown,
  ) {
    super(`Customer cart ${customerCartId} not found`, originalError);
  }
}

/** The item is not in this cart, or it is the only one and cannot be removed. */
export class CartItemNotFound extends ApiError {
  constructor(
    readonly itemId: string,
    originalError?: unknown,
  ) {
    super(`Item ${itemId} not found in this cart`, originalError);
  }
}

/** One of the scans has no live quote at this shop: never approved, rejected, or the quote lapsed. `reason` is the sentence to show. */
export class ScansNotPurchasable extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** No payment could be opened: an empty or expired cart, or a shop with no active payment provider. `reason` is the sentence to show. */
export class CheckoutRefused extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** The store's payment account is not ready for this: onboarding unfinished, disconnected, or a branch without an address. `reason` is the sentence to show. */
export class PaymentsNotReady extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** Sales tax could not be switched on: no branch carries a complete address. `reason` is the sentence to show. */
export class TaxCollectionRefused extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** The handle is held by another shop. */
export class SlugTaken extends ApiError {
  constructor(
    readonly slug: string,
    originalError?: unknown,
  ) {
    super(`The address "${slug}" is already taken`, originalError);
  }
}

/** No shop page at this handle. */
export class ShopPageNotFound extends ApiError {
  constructor(
    readonly slug: string,
    originalError?: unknown,
  ) {
    super(`No shop page at ${slug}`, originalError);
  }
}

/** The route needs a signed-in account; the caller is a storefront guest. */
export class RegisteredAccountRequired extends ApiError {
  constructor(originalError?: unknown) {
    super("Sign in with a HobbyScan account to continue", originalError);
  }
}

/** Not an imported shop, already claimed, or with no contact address on file. `reason` is the sentence to show. */
export class ShopNotClaimable extends ApiError {
  constructor(
    readonly storeId: string,
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

export class LocationNotFound extends ApiError {
  constructor(
    readonly locationId: string,
    originalError?: unknown,
  ) {
    super(`Location ${locationId} not found in this shop`, originalError);
  }
}

export class StreamNotFound extends ApiError {
  constructor(
    readonly streamId: string,
    originalError?: unknown,
  ) {
    super(`Stream ${streamId} not found`, originalError);
  }
}

/**
 * A live-break refusal with a machine-readable `code` (`not_found`, `slots_taken`,
 * `not_pending`, ...) and whatever the backend attached beside it in `details`.
 * `message` is the reason alone, the sentence to show; the code is for a view
 * that reacts to one refusal differently.
 */
export class LiveRefused extends ApiError {
  constructor(
    readonly code: string,
    readonly reason: string,
    readonly details: Record<string, unknown>,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/**
 * No directory page at this address: an unknown place, or a real one with no
 * shop in it. The backend does not distinguish them, and neither does the
 * storefront — both render the same 404.
 */
export class DirectoryPageNotFound extends ApiError {
  constructor(
    readonly path: string,
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(`No directory page at ${path}: ${reason}`, originalError);
  }
}

/**
 * The claim invitation cannot be acted on: it was already redeemed, revoked or
 * expired, or the shop is not eligible for one. `reason` is the sentence to show.
 */
export class ClaimInvitationRefused extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** The shop cannot be deleted: it has claim-invitation history, or it has taken money. `reason` is the sentence to show. */
export class ShopDeletionRefused extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

export class MarketingContactNotFound extends ApiError {
  constructor(
    readonly contactId: string,
    originalError?: unknown,
  ) {
    super(`Marketing contact ${contactId} not found`, originalError);
  }
}

export class MarketingTemplateNotFound extends ApiError {
  constructor(
    readonly templateId: string,
    originalError?: unknown,
  ) {
    super(`Marketing template ${templateId} not found`, originalError);
  }
}

export class MarketingCampaignNotFound extends ApiError {
  constructor(
    readonly campaignId: string,
    originalError?: unknown,
  ) {
    super(`Marketing campaign ${campaignId} not found`, originalError);
  }
}

/**
 * The marketing record is in a state this action cannot apply to: a campaign
 * that is not startable, not sending or already finished, or the shop-claim
 * template, which cannot be deleted. `reason` is the sentence to show.
 */
export class MarketingActionRefused extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** HobbyScan staff have not switched `capability` on for the shop yet. `reason` is the sentence to show. */
export class CapabilityRequired extends ApiError {
  constructor(
    readonly capability: string,
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/**
 * The shop takes payment through the other provider: a Shopify shop asked for a
 * Stripe route, or a shop holding one provider tried to connect the other.
 * `requiredMode` is what the route needs; `reason` is the sentence to show.
 */
export class WrongCommerceMode extends ApiError {
  constructor(
    readonly requiredMode: "SHOPIFY" | "STRIPE",
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** The shop may not host shows until HobbyScan staff switch hosting on for it. `reason` is the sentence to show. */
export class HostingOff extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** The show has no such kind of table. */
export class TableKindNotFound extends ApiError {
  constructor(
    readonly kindId: string,
    originalError?: unknown,
  ) {
    super(`Table kind ${kindId} not found`, originalError);
  }
}

/** The show has no such kind of ticket. */
export class TicketKindNotFound extends ApiError {
  constructor(
    readonly kindId: string,
    originalError?: unknown,
  ) {
    super(`Ticket kind ${kindId} not found`, originalError);
  }
}

/** The change would drop tables shops have already taken. `reason` is the sentence to show. */
export class TablesTaken extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** The shop has no such order for a table. */
export class TableOrderNotFound extends ApiError {
  constructor(
    readonly orderId: string,
    originalError?: unknown,
  ) {
    super(`Table order ${orderId} not found`, originalError);
  }
}

/**
 * The shop could not take a table, or give one up: it hosts the show, the show is
 * over, it already has a table there or one waiting for payment, the host cannot take
 * payments, none of the kind is left, or the order is no longer waiting for payment.
 * `reason` is the sentence to show.
 */
export class TableClaimRefused extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/**
 * A reservation's code could not be redeemed: no such code, unpaid or refunded, another
 * shop has it, the shop hosts the show or already has a table there. `reason` is the
 * sentence to show.
 */
export class ReservationRedeemRefused extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** The host cancelled the show, so nothing about it changes again. `reason` is the sentence to show. */
export class ShowCancelled extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** The show has no such table. */
export class TableNotFound extends ApiError {
  constructor(
    readonly tableId: string,
    originalError?: unknown,
  ) {
    super(`Table ${tableId} not found`, originalError);
  }
}

/**
 * The host could not take a table back: no shop holds it, a shop is paying for it,
 * its payment has no Stripe record, or Stripe refused the refund, in which case
 * nothing changed. `reason` is the sentence to show.
 */
export class TableRefundRefused extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** An import run was refused: another one is still going, or this staff member started six in the last hour. */
export class EventImportRefused extends ApiError {
  constructor(
    readonly reason: string,
    originalError?: unknown,
  ) {
    super(reason, originalError);
  }
}

/** Another card of the shop already is what the edit asked this one to become. */
export class CardIdentityTaken extends ApiError {
  constructor(
    readonly details: string,
    originalError?: unknown,
  ) {
    super(details, originalError);
  }
}

/** Fewer units are free than the cart asked for; nothing was held. `message` is the sentence to show. */
export class StockShortage extends ApiError {
  constructor(
    readonly available: number,
    message: string,
    originalError?: unknown,
  ) {
    super(message, originalError);
  }

  static from(e: ValidationError): StockShortage | null {
    if (e.code !== ErrorCode.InsufficientStock) return null;
    return new StockShortage(Number(e.details.available ?? 0), e.detailedDesc, e);
  }
}

/** An offer that is not, or no longer, for sale in this shop. `message` is the sentence to show. */
export class OfferNotForSale extends ApiError {
  static from(e: ValidationError): OfferNotForSale | null {
    if (e.code !== ErrorCode.NotForSale && e.incorrectValue !== "offers" && e.incorrectValue !== "offerId") return null;
    return new OfferNotForSale(e.detailedDesc, e);
  }
}
